/**
 * Phase 20 — one campaign group's auto-send switch and Resume, shared by
 * the country's groups table and the open group's board header: LIVE asks
 * first (real SMS — the caller shows the confirm while `confirming`, saying
 * `confirmMode`: real, or made up on a test server), OFF only makes the
 * group's drafts wait for a person, SIMULATED (stored as shadow) goes at
 * once — it sends nothing; Resume lifts its stop-loss halt for the day.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';
import type { DropGenAutoSendMode, DropGenGroupSendStatus } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import { useAutoSendMutations } from '../use-autosend-mutations';
import { refusalWords } from '../all/refusal-words';
import { liveKind, modePatch, useModeRights } from './mode-options';

const TOAST: Record<DropGenAutoSendMode, (name: string) => string> = {
  on: (name) => `${name} is Live — each draft goes out at its time`,
  shadow: (name) => `${name} is Simulated — it says what it would have sent, nothing goes out`,
  off: (name) => `Auto-send off for ${name} — its drafts wait for you`,
};

export function useGroupSwitch(countryKey: string, group: DropGenGroupSendStatus) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { update, resumeLayer } = useAutoSendMutations(countryKey);
  const server = useModeRights().server;
  const [pending, setPending] = useState<'on' | null>(null);
  const set = async (mode: DropGenAutoSendMode) => {
    try {
      await update.mutateAsync({ id: group.masterId, patch: modePatch(mode) });
      success(TOAST[mode](group.name));
    } catch (err) {
      error(mode === 'off' ? 'Could not switch Auto-send off' : `Auto-send stays as it was for ${group.name}`, refusalWords(getApiErrorMessage(err)));
    }
    setPending(null);
  };
  const resume = async () => {
    try {
      await resumeLayer.mutateAsync(group.masterId);
      success(`${group.name} sends again — its stop-loss does not halt it again today`);
    } catch (err) {
      error(`${group.name} stays halted`, getApiErrorMessage(err));
    }
  };
  return {
    canSwitch: hasPermission('drop_generator.autosend') && !update.isPending,
    canResume: hasPermission('drop_generator.kill'),
    busy: update.isPending,
    resuming: resumeLayer.isPending,
    confirming: pending !== null,
    /** What the confirm says Live does here: real SMS, or made up on a test server. */
    confirmMode: liveKind(server),
    /** The pills' pick: LIVE asks first; OFF and SIMULATED go at once. */
    pick: (mode: DropGenAutoSendMode) => (mode === 'on' ? setPending('on') : void set(mode)),
    confirm: () => void set(pending ?? 'on'),
    cancel: () => setPending(null),
    resume: () => void resume(),
  };
}
